/* eslint-disable ordered-imports/ordered-imports */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import BackArrow from "@NA/Icons/backArrow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { createRef, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ADIColors from "@NA/Utils/ADIColors";
import ListItem from "@insite/mobius/Lists/ListItem";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
    returnUrl?: string;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext & HasHistory;

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselContentEmptyCellGridItem?: GridItemProps;
    carouselCellGridItem?: GridItemProps;
    carouselContentContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    attributeLabelsCellGridItem?: GridItemProps;
    attributeLabelsGridContainer?: GridContainerProps;
    attributeLabelGridItem?: GridItemProps;
    attributeLabelText?: TypographyPresentationProps;
    attributeValuesCellGridItem?: GridItemProps;
    attributeValuesWrapper?: InjectableCss;
    attributeValuesContainer?: InjectableCss;
    attributeValuesSlidesContainer?: InjectableCss;
    attributeValueSlide?: InjectableCss;
    attributeValueSlideInner?: InjectableCss;
    attributeValuesGridContainer?: GridContainerProps;
    attributeValueGridItem?: GridItemProps;
    attributeValueText?: TypographyPresentationProps;
    productDetails?: GridItemProps;
    stickyProductDetails?: GridItemProps;
    navigationLink?: LinkPresentationProps;
    customAttributesWrapper?: InjectableCss;
    customAttributes?: InjectableCss;
    innerListItemStyle?: InjectableCss;
    carouselContentCellGridContainer?: InjectableCss;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselContentContainer: {
        gap: 0,
    },
    carouselContentCellGridContainer: {
        css: css`
            margin: 0;
        `,
    },
    carouselContentEmptyCellGridItem: {
        width: 3,
    },
    carouselCellGridItem: {
        width: 9,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeLabelsCellGridItem: {
        width: 3,
        css: css`
            z-index: 1;
        `,
    },
    attributeLabelsGridContainer: {
        gap: 0,
        css: css`
            z-index: 999;
            div[calss^="GridOffset"] {
                max-width: 100%;
            }
        `,
    },
    attributeLabelGridItem: {
        width: 12,
        css: css`
            padding: 0.8rem 2.1rem;
            min-height: 50px;
            &:nth-child(odd) {
                background-color: ${ADIColors.tableRowGray};
            }
        `,
    },
    attributeLabelText: {
        ellipsis: true,
        css: css`
            color: ${ADIColors.darkBlue1};
            font-size: 16px;
            font-weight: 900;
        `,
    },
    attributeValuesCellGridItem: {
        width: 9,
    },
    attributeValuesWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    attributeValuesContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    attributeValuesSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    attributeValueSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    attributeValueSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeValuesGridContainer: {
        gap: 0,
        css: css`
            div[calss^="GridOffset"] {
                max-width: 100%;
            }
        `,
    },
    attributeValueGridItem: {
        width: 12,
        css: css`
            border-bottom-style: solid;
            border-bottom-width: 0px;
            padding: 0.8rem 20px;
            min-height: 50px;
            list-style: none;
            &:nth-child(odd) {
                background-color: ${ADIColors.tableRowGray};
            }
        `,
    },
    attributeValueText: {
        ellipsis: true,
        size: 14,
        color: `${ADIColors.black}`,
        css: css`
            font-family: "Open Sans", sans-serif;
        `,
    },
    productDetails: {
        css: css`
            background: ${ADIColors.white};
            width: 100%;
        `,
    },
    stickyProductDetails: {
        css: css`
            background: ${ADIColors.white};
            position: fixed;
            top: 115px;
            z-index: 2;
            padding-top: 15px;
            width: 100%;
        `,
    },
    navigationLink: {
        icon: { iconProps: { src: BackArrow, size: 12 } },
        typographyProps: { size: 15 },
        css: css`
            border-radius: 7px;
            border: solid 2px ${ADIColors.blue1};
            height: 40px;
            width: 157px;
            background: none;
            padding: 0;
            text-transform: uppercase;
            justify-content: center;
            color: ${ADIColors.blue1};
        `,
    },
    customAttributesWrapper: {
        css: css`
            width: 100%;
            clear: both;
            display: block;
        `,
    },
    customAttributes: {
        css: css`
            width: 100%;
            display: flex;
            float: left;
            white-space: normal;
            &:nth-child(odd) {
                background-color: ${ADIColors.white};
            }
            &:nth-child(even) {
                background-color: ${ADIColors.tableRowGray};
            }
            > ${TypographyStyle} {
                flex: 1;
                padding: 0.8rem 20px;
                min-height: 50px;
                white-space: normal;
            }
        `,
    },
    innerListItemStyle: {
        css: css`
            border-bottom-style: solid;
            border-bottom-width: 0px;
            padding: 0.8rem 20px;
            min-height: 50px;
            list-style: none;
            &:nth-child(odd) {
                background-color: ${ADIColors.tableRowGray};
            }
            min-height: 50px;
            flex: 1;
            background: transparent !important;
            font-size: inherit;
            padding: 0.8rem 20px;
            ${TypographyStyle} {
                white-space: normal;
            }
        `,
    },
};

const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    title,
    hideTitle,
    maxNumberOfColumns,
    slideDetails,
    returnUrl,
    history,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [emblaSecondRef, emblaSecond] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [offset, setOffset] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            // eslint-disable-next-line no-useless-return
            return;
        }
    };

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 3;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = useState(getDraggable());

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!emblaSecond) {
            return;
        }

        emblaSecond.on("init", setCanScroll);
        emblaSecond.on("select", setCanScroll);
    }, [emblaSecond]);

    useEffect(() => {
        if (!embla || !emblaSecond || !slideDetails || slideDetails.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: true,
        });
        emblaSecond.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: true,
        });
        setCanScroll();
    }, [embla, emblaSecond, slidesToScroll, draggable, slideDetails]);

    useEffect(() => {
        window.onscroll = () => {
            const check = window.parent.document.getElementById("siteIFrame");
            if (!check) {
                setOffset(window.pageYOffset);
            } else {
                setOffset(2000);
            }
        };
    }, []);

    const stickyAttrStyles = {
        css: css`
            padding-top: ${height}px;
        `,
    };

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                    dangerouslySetInnerHTML={{ __html: productAttributeValue.map(o => o.valueDisplay).join(", ") }}
                />
            );
        }

        return <>&nbsp;</>;
    };

    const goBack = () => {
        // if (returnUrl) {
        //     history.push(returnUrl);
        // }
        window.history.back();
    };

    useEffect(() => {
        if (!ref?.current?.clientHeight) {
            return;
        }
        setHeight(ref?.current?.clientHeight);
    }, [ref?.current?.clientHeight]);

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    const compcount = slideDetails.products.length;

    const getBrandUrl = (product: any) => {
        if (product?.brand?.detailPagePath) {
            const detailPagePath = product.brand.detailPagePath.toLowerCase();
            return detailPagePath.startsWith("/catalog") ? detailPagePath : `/catalog${detailPagePath}`;
        }
        return '/catalog';
    };
    
    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {hideTitle ? (
                <VisuallyHidden>
                    <Typography {...styles.titleText}>{title}</Typography>
                </VisuallyHidden>
            ) : (
                <Typography {...styles.titleText}>{title}</Typography>
            )}

            <GridContainer {...styles.mainContainer} data-test-selector="productCompareCarousel_gridContainer">
                <GridItem {...styles.carouselGridItem}>
                    <GridContainer {...styles.carouselContentContainer}>
                        <GridItem
                            width={12}
                            {...(offset > 100 ? { ...styles.stickyProductDetails } : { ...styles.productDetails })}
                        >
                            <StyledWrapper ref={ref} style={{ width: "100%" }}>
                                <GridContainer gap={0} {...styles.carouselContentCellGridContainer}>
                                    <GridItem {...styles.carouselContentEmptyCellGridItem}>
                                        {returnUrl && (
                                            <Link
                                                {...styles.navigationLink}
                                                onClick={goBack}
                                                data-test-selector="productCompareGoBackButton"
                                            >
                                                {translate("Back")}
                                            </Link>
                                        )}
                                    </GridItem>
                                    <GridItem {...styles.carouselCellGridItem}>
                                        <StyledWrapper {...styles.carouselWrapper}>
                                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                                <StyledWrapper
                                                    {...styles.carouselSlidesContainer}
                                                    data-test-selector="slides"
                                                >
                                                    {slideDetails.slides.map(slide => (
                                                        <StyledWrapper
                                                            {...styles.carouselSlide}
                                                            key={slide.id}
                                                            style={{ width: `33.3333%` }}
                                                        >
                                                            <StyledWrapper
                                                                {...styles.carouselSlideInner}
                                                                id={`slide_${slide.id}`}
                                                                data-test-selector="slideContainer"
                                                            >
                                                                {slide.renderComponent({})}
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    ))}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem width={12} {...(offset > 100 ? { ...stickyAttrStyles } : "")}>
                            <GridContainer gap={0}>
                                <GridItem width={3} {...styles.attributeLabelsCellGridItem}>
                                    <GridContainer {...styles.attributeLabelsGridContainer}>
                                        <GridItem {...styles.attributeLabelGridItem}>
                                            <Typography {...styles.attributeLabelText}>{translate("Brand")}</Typography>
                                        </GridItem>
                                        <GridItem {...styles.attributeLabelGridItem}>
                                            <Typography {...styles.attributeLabelText}>
                                                {translate("ADI Part Number")}
                                            </Typography>
                                        </GridItem>
                                        <GridItem {...styles.attributeLabelGridItem}>
                                            <Typography {...styles.attributeLabelText}>
                                                {translate("Model Number")}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                <GridItem width={9} {...styles.attributeValuesCellGridItem}>
                                    <StyledWrapper {...styles.attributeValuesWrapper}>
                                        <StyledWrapper {...styles.attributeValuesContainer} ref={emblaSecondRef}>
                                            <StyledWrapper
                                                {...styles.attributeValuesSlidesContainer}
                                                data-test-selector="attribute-value-slides"
                                            >
                                                {slideDetails.products.map(product => (
                                                    <StyledWrapper
                                                        {...styles.attributeValueSlide}
                                                        key={`${product.id}-attributes`}
                                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                                    >
                                                        <StyledWrapper
                                                            {...styles.attributeValueSlideInner}
                                                            id={`product-attribute_${product.id}`}
                                                            data-test-selector="slideAttributesContainer"
                                                            data-test-key={product.id}
                                                        >
                                                            <GridContainer {...styles.attributeValuesGridContainer}>
                                                                <GridItem {...styles.attributeValueGridItem}>
                                                                    <Link href={getBrandUrl(product)}>
                                                                        {product.brand?.name}
                                                                    </Link>
                                                                </GridItem>
                                                                <GridItem {...styles.attributeValueGridItem}>
                                                                    {product.productNumber}
                                                                </GridItem>
                                                                <GridItem {...styles.attributeValueGridItem}>
                                                                    {product.manufacturerItem}
                                                                </GridItem>
                                                            </GridContainer>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ))}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem
                                    width={compcount === 1 ? 6 : compcount === 2 ? 9 : 12}
                                    {...styles.customAttributesWrapper}
                                >
                                    {slideDetails.attributeTypes.map(attribute => {
                                        if (attribute.name !== "Availability") {
                                            return (
                                                // eslint-disable-next-line react/jsx-key
                                                <StyledWrapper {...styles.customAttributes}>
                                                    <Typography
                                                        id={`carousel-${id}_attribute-label-${attribute.id}`}
                                                        {...styles.attributeLabelText}
                                                    >
                                                        {attribute.label || attribute.name}
                                                    </Typography>

                                                    {slideDetails.products.map(product => (
                                                        <ListItem
                                                            {...styles.innerListItemStyle}
                                                            key={`${product.id}-attribute-${attribute.id}`}
                                                        >
                                                            {getProductAttributeValue(product, attribute)}
                                                        </ListItem>

                                                        // <ListItem
                                                        //     key={`${product.id}-attribute-${attribute.id}`}
                                                        //     {...styles.attributeValueGridItem}
                                                        // >
                                                        //     {getProductAttributeValue(product, attribute)}
                                                        // </ListItem>
                                                    ))}
                                                </StyledWrapper>
                                            );
                                        }
                                    })}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default withIsInShell(withTheme(withHistory(Carousel)));
